export function parseBoxShadow(shadow) {
  if (!shadow) return null;

  // Split multiple shadows by commas not inside parentheses
  const parts = shadow.match(/(?:[^(,]+|\([^)]*\))+/g);

  return parts.map((shadowPart) => {
    const obj = {
      inset: false,
      offsetX: null,
      offsetY: null,
      blurRadius: null,
      spreadRadius: null,
      color: null,
    };

    let str = shadowPart.trim();

    // Detect and remove "inset"
    if (str.includes("inset")) {
      obj.inset = true;
      str = str.replace(/\binset\b/, "").trim();
    }

    // Extract color (rgba, rgb, hsl, hex)
    const colorMatch = str.match(
      /(rgba?\([^)]+\)|hsla?\([^)]+\)|#[0-9a-fA-F]{3,8})/
    );
    if (colorMatch) {
      obj.color = convertColorToHex(colorMatch[0]);
      str = str.replace(colorMatch[0], "").trim();
    }

    // Extract length values
    const values = str.match(/-?\d+(\.\d+)?(px|em|rem|%)?/g) || [];

    obj.offsetX = values[0] || 0;
    obj.offsetY = values[1] || 0;
    obj.blurRadius = values[2] || 0;
    obj.spreadRadius = values[3] || 0;

    // Ensure "px" suffix if numeric
    for (const key of ["offsetX", "offsetY", "blurRadius", "spreadRadius"]) {
      if (obj[key] && /^\d+$/.test(obj[key])) obj[key] += "px";
    }

    return obj;
  });
}

export function unParseBoxShadow(shadowObj) {
  return `${shadowObj.inset ? "inset" : ""} ${shadowObj.offsetX} ${
    shadowObj.offsetY
  } ${shadowObj.blurRadius} ${shadowObj.spreadRadius} ${shadowObj.color}`;
}

/**
 * Convert any color format to hex
 * Supports: rgb, rgba, hsl, hsla, hex (3/6/8 digits), named colors
 * @param {string} color - Color in any format
 * @returns {string} Hex color with alpha (#RRGGBBAA or #RRGGBB)
 */
export function convertColorToHex(color) {
  if (!color) return null;

  const trimmed = color.trim();

  // Already hex - normalize it
  if (trimmed.startsWith("#")) {
    return normalizeHex(trimmed);
  }

  // RGB/RGBA
  const rgbaMatch = trimmed.match(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)/
  );
  if (rgbaMatch) {
    const [, r, g, b, a] = rgbaMatch;
    return rgbToHex(
      parseInt(r),
      parseInt(g),
      parseInt(b),
      a ? parseFloat(a) : 1
    );
  }

  // HSL/HSLA
  const hslaMatch = trimmed.match(
    /hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*(?:,\s*([\d.]+)\s*)?\)/
  );
  if (hslaMatch) {
    const [, h, s, l, a] = hslaMatch;
    return hslToHex(
      parseFloat(h),
      parseFloat(s),
      parseFloat(l),
      a ? parseFloat(a) : 1
    );
  }

  // Named colors - use canvas to convert
  return namedColorToHex(trimmed);
}

/**
 * Normalize hex color (expand 3/4 digit to 6/8 digit)
 */
function normalizeHex(hex) {
  hex = hex.replace("#", "");

  // 3 digit hex -> 6 digit
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  // 4 digit hex -> 8 digit (with alpha)
  if (hex.length === 4) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  return "#" + hex.toUpperCase();
}

/**
 * Convert RGB(A) to hex
 */
function rgbToHex(r, g, b, a = 1) {
  const toHex = (n) => {
    const hex = Math.max(0, Math.min(255, Math.round(n))).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  let hex = "#" + toHex(r) + toHex(g) + toHex(b);

  // Add alpha channel if not fully opaque
  if (a < 1) {
    const alphaHex = toHex(Math.round(a * 255));
    hex += alphaHex;
  }

  return hex.toUpperCase();
}

/**
 * Convert HSL(A) to hex
 */
function hslToHex(h, s, l, a = 1) {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return rgbToHex(
    Math.round(r * 255),
    Math.round(g * 255),
    Math.round(b * 255),
    a
  );
}

/**
 * Convert named color to hex using canvas
 */
function namedColorToHex(color) {
  // Create a canvas element
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 1;
  const ctx = canvas.getContext("2d");

  // Set the color and get the computed value
  ctx.fillStyle = color;
  const computedColor = ctx.fillStyle;

  // The computed color will be in rgb() or rgba() format
  return convertColorToHex(computedColor);
}
